import {
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class DeleteSurveyLanguageRPC implements
  LeoRPC<DeleteSurveyLanguageRPC.Request, DeleteSurveyLanguageRPC.Response, DeleteSurveyLanguageRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteSurveyLanguageRPC.Request
  ): Promise<LeoRPCResult<DeleteSurveyLanguageRPC.Response, DeleteSurveyLanguageRPC.Errors.Errors>>;
}

export namespace DeleteSurveyLanguageRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    languageId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteSurveyLanguageRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly languageId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    languageId: LeoUUID
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.languageId = languageId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const languageId = getLeoUuid(dto, "languageId", InvalidRequestLanguageIdError);
      return new Request(
        projectId,
        surveyId,
        languageId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          languageId: this.languageId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteSurveyLanguageRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',languageId='${this.languageId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteSurveyLanguageRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidLanguageId = "INVALID_LANGUAGE_ID",
    BaseLanguageCannotBeModified = "BASE_LANGUAGE_CANNOT_BE_MODIFIED",
    LanguageInUse = "LANGUAGE_IN_USE",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidLanguageIdDTO {
      code: string;
    }
    
    export class InvalidLanguageId extends LeoRPCError<RPCError> {
      readonly isInvalidLanguageId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidLanguageId;
        super(code);
      }
    
    
      toDTO(): InvalidLanguageIdDTO {
        const dto: InvalidLanguageIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidLanguageId.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.INVALID_LANGUAGE_ID()`;
    };
    
    
    export interface BaseLanguageCannotBeModifiedDTO {
      code: string;
    }
    
    export class BaseLanguageCannotBeModified extends LeoRPCError<RPCError> {
      readonly isBaseLanguageCannotBeModified: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.BaseLanguageCannotBeModified;
        super(code);
      }
    
    
      toDTO(): BaseLanguageCannotBeModifiedDTO {
        const dto: BaseLanguageCannotBeModifiedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    BaseLanguageCannotBeModified.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.BASE_LANGUAGE_CANNOT_BE_MODIFIED()`;
    };
    
    
    export interface LanguageInUseDTO {
      code: string;
    }
    
    export class LanguageInUse extends LeoRPCError<RPCError> {
      readonly isLanguageInUse: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.LanguageInUse;
        super(code);
      }
    
    
      toDTO(): LanguageInUseDTO {
        const dto: LanguageInUseDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    LanguageInUse.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.LANGUAGE_IN_USE()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface TranslationUpdateInProgressDTO {
      code: string;
    }
    
    export class TranslationUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isTranslationUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.TranslationUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): TranslationUpdateInProgressDTO {
        const dto: TranslationUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    TranslationUpdateInProgress.prototype.toString = function toString(): string {
      return `DeleteSurveyLanguageRPC.TRANSLATION_UPDATE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidLanguageId | BaseLanguageCannotBeModified | LanguageInUse | ProjectAlreadyArchived | TranslationUpdateInProgress;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidLanguageIdDTO | BaseLanguageCannotBeModifiedDTO | LanguageInUseDTO | ProjectAlreadyArchivedDTO | TranslationUpdateInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidLanguageId:
          return new InvalidLanguageId();
        case RPCError.BaseLanguageCannotBeModified:
          return new BaseLanguageCannotBeModified();
        case RPCError.LanguageInUse:
          return new LanguageInUse();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.TranslationUpdateInProgress:
          return new TranslationUpdateInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends DeleteSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestLanguageIdError extends DeleteSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLanguageIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidLanguageIdError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidLanguageIdError";
  }
}

export class InvalidResponseBaseLanguageCannotBeModifiedError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBaseLanguageCannotBeModifiedError";
  }
}

export class InvalidResponseLanguageInUseError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseLanguageInUseError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseTranslationUpdateInProgressError extends DeleteSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTranslationUpdateInProgressError";
  }
}

